import { useToast } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import alb from 'services/api/alb';

import { QueryTypeModule } from '../../../../services/queryClient/wrapperHooks/genericTables/useGetCheckTableDataWithFilters';
import { PAGINATION_SIZE_LIST } from '../../../../utils/consts/pagination';
import {
	IFilterBody,
	ISelectedFilterBody,
} from '../../../filter/models/TableFilterModels';

export interface IPaginationData {
	module: QueryTypeModule;
	page?: number;
	size?: number;
	filters?: ISelectedFilterBody[] | any[];
	preFilteredRows?: any;
	order: string[][];
	[key: string]: any;
}
export interface IRequestCheckTableBody<BodyType> {
	pagination?: {
		page: number | undefined;
		size: number;
		order: string[][];
	};
	filters: any[] | ISelectedFilterBody[] | undefined;
	edited?: BodyType[];
	[key: string]: any;
}

interface IFetchDataByFiltersOutput<T> {
	data: Array<T>;
	filterMap: IFilterBody[];
	dependencias: string[];
	count: number;
}
interface IFetchDataByFiltersOutputTotal<T> {
	total?: Partial<T>;
}
export const fetchDataByFiltersAgGrid = async <T, BodyType>(
	module: QueryTypeModule,
	body: IRequestCheckTableBody<BodyType>
): Promise<IFetchDataByFiltersOutput<T>> => {
	const url = `/${module}/editable-view`;
	const dataRows = await alb.post(url, body).then(res => res.data);
	return dataRows;
};

export const useGetDataGridTableWithFilters = <T extends unknown, BodyType>(
	{
		module,
		page,
		size,
		order,
		filters,
		edited,
		nonexistentHierarchyCombinations,
		status,
		...other
	}: IPaginationData,
	onSuccess: VoidFunction
) => {
	const toast = useToast();
	return useQuery(
		[
			`${module}-list`,
			page,
			size,
			nonexistentHierarchyCombinations,
			JSON.stringify(filters),
			JSON.stringify(other),
			JSON.stringify(order),
			status,
		],
		() => {
			const requestBody: IRequestCheckTableBody<BodyType> = {
				pagination: {
					page,
					size: size || PAGINATION_SIZE_LIST[0],
					order,
				},
				filters: filters,
				edited,
				nonexistent_hierarchy_combinations:
					nonexistentHierarchyCombinations,
				status,
				...other,
			};

			return fetchDataByFiltersAgGrid<T, BodyType>(module, requestBody);
		},

		{
			onError: () => {
				toast({
					title: 'Erro ao buscar dados!',
					description:
						'Ocorreu um erro ao buscar os dados para listagem.',
					status: 'error',
					duration: 8000,
					position: 'bottom-left',
					isClosable: true,
				});
			},
			refetchOnWindowFocus: false,
			retry: false,
			enabled: !!module && status && status !== 'none',
			onSuccess,
		}
	);
};

export const fetchDataByFiltersAgGridTotal = async <T, BodyType>(
	module: QueryTypeModule,
	body: IRequestCheckTableBody<BodyType>
): Promise<IFetchDataByFiltersOutputTotal<T>> => {
	const urlTotal = `/${module}/editable-view-total`;
	const totalRows = await alb.post(urlTotal, body).then(res => res.data);
	return {
		total: totalRows,
	};
};

export const useGetDataGridTableWithFiltersTotal = <
	T extends unknown,
	BodyType
>(
	{
		module,
		page,
		size,
		order,
		filters,
		edited,
		status,
		...other
	}: IPaginationData,
	onSuccess: VoidFunction
) => {
	const toast = useToast();
	return useQuery(
		[
			`${module}-list`,
			page,
			size,
			JSON.stringify(filters),
			JSON.stringify(other),
			JSON.stringify(order),
		],
		() => {
			const requestBody: IRequestCheckTableBody<BodyType> = {
				pagination: {
					page,
					size: size || PAGINATION_SIZE_LIST[0],
					order,
				},
				filters: filters,
				edited,
				...other,
			};

			return fetchDataByFiltersAgGridTotal<T, BodyType>(
				module,
				requestBody
			);
		},

		{
			onError: e => {
				toast({
					title: 'Erro ao buscar dados!',
					description:
						'Ocorreu um erro ao buscar os dados para listagem.',
					status: 'error',
					duration: 8000,
					position: 'bottom-left',
					isClosable: true,
				});
			},
			refetchOnWindowFocus: false,
			retry: false,
			enabled: !!module && status && status !== 'none',
			onSuccess,
		}
	);
};

export type ViewTableStatus = 'saved_zeroed' | 'none' | 'saved_with_values';
interface IRequestCheckTableBodyIsStatus {
	cycle_id: number;
	company_area_id?: number;
}
const fetchDataByFiltersAgGridIsStatus = async (
	module: QueryTypeModule,
	body: IRequestCheckTableBodyIsStatus
): Promise<ViewTableStatus> => {
	const url = `/${module}/editable-view-is-status`;
	const { data } = await alb.post<{ status: ViewTableStatus }>(url, body);
	return data.status;
};

export const useGetStatus = (
	module: QueryTypeModule,
	other: IRequestCheckTableBodyIsStatus
) => {
	return useQuery(
		[`is-status-editable`, module, JSON.stringify(other)],
		() => {
			return fetchDataByFiltersAgGridIsStatus(module, other);
		},
		{
			refetchOnWindowFocus: false,
			retry: false,
			enabled: !!module && !!other,
		}
	);
};
